import { z } from 'zod';
import { ChannelId } from './common.js';

/**
 * One thing that happened in a channel's moderation, as a moderator sees it
 * (ADR 0050 §5). Live only: nothing here is ever stored.
 *
 *   automod_hold / automod_update   the AutoMod queue, and its decisions
 *   automod_settings / automod_terms  the rules changed
 *   mod_action                      any moderator action (channel.moderate)
 *   warning / warning_ack           a warning sent, and acknowledged
 *   unban_request / unban_resolved  the unban queue
 *   suspicious_message / suspicious_update  monitored or restricted users
 *   shield_on / shield_off          shield mode
 */
export const MOD_FEED_KINDS = [
  'automod_hold', 'automod_update', 'automod_settings', 'automod_terms',
  'mod_action', 'warning', 'warning_ack',
  'unban_request', 'unban_resolved',
  'suspicious_message', 'suspicious_update',
  'shield_on', 'shield_off',
] as const;

const Person = z.object({ id: z.string(), login: z.string(), name: z.string() });

export const ModFeedEvent = z.object({
  /** Monotonic within one feed, and the SSE id a reconnect resumes from. */
  id: z.string(),
  channel_id: ChannelId,
  kind: z.enum(MOD_FEED_KINDS),
  at: z.string().datetime(),
  /** Who did it: the moderator, or the chatter whose message was held. */
  actor: Person.nullable(),
  /** Who it was done to, when that is somebody else. */
  target: Person.nullable(),
  /**
   * What happened, in a word Twitch uses: `ban`, `timeout`, `delete`,
   * `approved`, `denied`, `restricted`… Null where the kind says it all.
   */
  action: z.string().nullable(),
  /** The message, the reason, or the request, when there is one. */
  text: z.string().nullable(),
  /**
   * What an automod or unban item is ABOUT, so a hold and its later update can
   * be matched: Twitch's message id, or the unban request id.
   */
  subject_id: z.string().nullable(),
  /** Twitch's own event, unabridged, for anything the fields above leave out. */
  data: z.record(z.string(), z.unknown()),
});
export type ModFeedEvent = z.infer<typeof ModFeedEvent>;
export type ModFeedKind = (typeof MOD_FEED_KINDS)[number];

/** Allow a held AutoMod message into chat, or deny it (ADR 0050 §6). */
export const AutomodDecision = z.object({ action: z.enum(['allow', 'deny']) });
export type AutomodDecision = z.infer<typeof AutomodDecision>;

/**
 * Somebody on Twitch: by numeric id when the caller has it (a chat message
 * carries it), or by login when a person typed it (`/ban somebody`).
 */
export const TwitchUserRef = z.union([
  z.object({ id: z.string().regex(/^\d{1,20}$/) }),
  z.object({ login: z.string().regex(/^[A-Za-z0-9_]{1,25}$/) }),
]);
export type TwitchUserRef = z.infer<typeof TwitchUserRef>;

const Reason = z.string().trim().min(1).max(500);

/**
 * A moderator action, taken on Twitch as the person asking (ADR 0050 §6).
 * Timeouts are capped where Twitch caps them: two weeks.
 */
export const ModActionInput = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ban'), user: TwitchUserRef, reason: Reason.optional() }),
  z.object({
    type: z.literal('timeout'), user: TwitchUserRef,
    seconds: z.number().int().min(1).max(1_209_600), reason: Reason.optional(),
  }),
  z.object({ type: z.literal('unban'), user: TwitchUserRef }),
  z.object({ type: z.literal('delete'), message_id: z.string().min(1).max(128) }),
  z.object({ type: z.literal('clear') }),
  z.object({ type: z.literal('warn'), user: TwitchUserRef, reason: Reason }),
  z.object({
    type: z.literal('chat_settings'),
    settings: z.object({
      slow_mode: z.boolean().optional(),
      slow_mode_wait_time: z.number().int().min(3).max(120).nullable().optional(),
      follower_mode: z.boolean().optional(),
      follower_mode_duration: z.number().int().min(0).max(129_600).nullable().optional(),
      subscriber_mode: z.boolean().optional(),
      emote_mode: z.boolean().optional(),
      unique_chat_mode: z.boolean().optional(),
    }).refine((s) => Object.keys(s).length > 0, 'Change at least one setting.'),
  }),
  z.object({ type: z.literal('shield'), active: z.boolean() }),
  z.object({
    type: z.literal('announce'), message: z.string().trim().min(1).max(500),
    color: z.enum(['primary', 'blue', 'green', 'orange', 'purple']).optional(),
  }),
  z.object({ type: z.literal('shoutout'), to: TwitchUserRef }),
  z.object({
    type: z.literal('resolve_unban_request'), request_id: z.string().min(1).max(128),
    approve: z.boolean(), resolution_text: Reason.optional(),
  }),
]);
export type ModActionInput = z.infer<typeof ModActionInput>;

/** Who is banned or timed out on a channel, for completing /unban. */
export const BannedList = z.object({
  data: z.array(z.object({
    login: z.string(),
    /** Null for a permanent ban. */
    expires_at: z.string().datetime().nullable(),
  })),
});
export type BannedList = z.infer<typeof BannedList>;
